import React, { useState } from 'react';
import { useAuthStore } from '../../../stores';
import { MFASetupModal } from './MFASetupModal';
import './LoginPage.css';

interface LoginPageProps {
  onLoginSuccess?: () => void;
}

type AuthStep = 'login' | 'mfa' | 'newPassword' | 'mfaSetup';

export const LoginPage: React.FC<LoginPageProps> = ({ onLoginSuccess }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [mfaCode, setMfaCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [authStep, setAuthStep] = useState<AuthStep>('login');
  const [session, setSession] = useState<string>('');
  const [mfaSetupData, setMfaSetupData] = useState<{ secretCode: string; qrCodeUrl: string } | null>(null);

  const { login, verifyMFA, setNewPassword: setNewPasswordAction, setupMFA, setupMFAWithSession, verifyMFASetupWithSession } = useAuthStore();

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    
    if (!username || !password) {
      setError('Please enter both username and password');
      return;
    }

    setIsLoading(true);
    
    try {
      const result = await login(username, password);
      
      if (result.requiresMFA && result.session) {
        setSession(result.session);
        setAuthStep('mfa');
      } else if (result.requiresNewPassword && result.session) {
        setSession(result.session);
        setAuthStep('newPassword');
      } else {
        // Check if MFA setup is needed
        try {
          const mfaData = await setupMFA();
          setMfaSetupData(mfaData);
          setAuthStep('mfaSetup');
        } catch {
          // MFA already set up or not required
          onLoginSuccess?.();
        }
      }
    } catch (err: any) {
      setError(err.message || 'Login failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleMFAVerification = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!mfaCode || mfaCode.length !== 6) {
      setError('Please enter a 6-digit code');
      return;
    }

    setIsLoading(true);

    try {
      await verifyMFA(username, mfaCode, session);
      onLoginSuccess?.();
    } catch (err: any) {
      setError(err.message || 'Invalid MFA code. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleNewPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!newPassword || newPassword.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setIsLoading(true);

    try {
      const result = await setNewPasswordAction(username, newPassword, session);
      
      if (result.requiresMFASetup && result.session) {
        // Need to set up MFA - get QR code
        const mfaData = await setupMFAWithSession(result.session);
        setMfaSetupData(mfaData);
        setSession(mfaData.session); // Update session with new one from AssociateSoftwareToken
        setAuthStep('mfaSetup');
      } else {
        onLoginSuccess?.();
      }
    } catch (err: any) {
      setError(err.message || 'Failed to set new password. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleMFASetupVerify = async (code: string) => {
    await verifyMFASetupWithSession(username, code, session);
    onLoginSuccess?.();
  };

  const handleMFASetupCancel = () => {
    // Skip MFA setup for now, just log in
    onLoginSuccess?.();
  };

  return (
    <div 
      className="login-container"
      style={{
        backgroundImage: `url(${process.env.PUBLIC_URL}/images/background.png)`
      }}
    >
      <div className="login-card">
        {authStep === 'login' && (
          <>
            <h1 className="login-title">Welcome Back</h1>
            <p className="login-subtitle">Sign in to continue</p>
            
            <form onSubmit={handleLogin} className="login-form">
              <div className="form-group">
                <label htmlFor="username">Username</label>
                <input
                  id="username"
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="Enter your username"
                  disabled={isLoading}
                  autoComplete="username"
                />
              </div>

              <div className="form-group">
                <label htmlFor="password">Password</label>
                <input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  disabled={isLoading}
                  autoComplete="current-password"
                />
              </div>

              {error && <div className="error-message">{error}</div>}

              <button 
                type="submit" 
                className="login-button"
                disabled={isLoading}
              >
                {isLoading ? 'Signing in...' : 'Sign In'}
              </button>
            </form>
          </>
        )}

        {authStep === 'mfa' && (
          <>
            <h1 className="login-title">Two-Factor Authentication</h1>
            <p className="login-subtitle">Enter the code from your authenticator app</p>
            
            <form onSubmit={handleMFAVerification} className="login-form">
              <div className="form-group">
                <label htmlFor="mfa-code">Authentication Code</label>
                <input
                  id="mfa-code"
                  type="text"
                  value={mfaCode}
                  onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="000000"
                  maxLength={6}
                  disabled={isLoading}
                  autoComplete="off"
                  className="mfa-input"
                />
              </div>

              {error && <div className="error-message">{error}</div>}

              <button 
                type="submit" 
                className="login-button"
                disabled={isLoading || mfaCode.length !== 6}
              >
                {isLoading ? 'Verifying...' : 'Verify'}
              </button>

              <button
                type="button"
                onClick={() => {
                  setAuthStep('login');
                  setMfaCode('');
                  setError('');
                }}
                className="back-button"
                disabled={isLoading}
              >
                Back to Login
              </button>
            </form>
          </>
        )}

        {authStep === 'newPassword' && (
          <>
            <h1 className="login-title">Set New Password</h1>
            <p className="login-subtitle">Please choose a new password for your account</p>
            
            <form onSubmit={handleNewPassword} className="login-form">
              <div className="form-group">
                <label htmlFor="new-password">New Password</label>
                <input
                  id="new-password"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Enter new password"
                  disabled={isLoading}
                  autoComplete="new-password"
                />
                <small className="password-hint">
                  Must be at least 8 characters with uppercase, lowercase, number, and symbol
                </small>
              </div>

              <div className="form-group">
                <label htmlFor="confirm-password">Confirm Password</label>
                <input
                  id="confirm-password"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm new password"
                  disabled={isLoading}
                  autoComplete="new-password"
                />
              </div>

              {error && <div className="error-message">{error}</div>}

              <button 
                type="submit" 
                className="login-button"
                disabled={isLoading}
              >
                {isLoading ? 'Setting Password...' : 'Set Password'}
              </button>
            </form>
          </>
        )}
      </div>

      {authStep === 'mfaSetup' && mfaSetupData && (
        <MFASetupModal
          secretCode={mfaSetupData.secretCode}
          qrCodeUrl={mfaSetupData.qrCodeUrl}
          onVerify={handleMFASetupVerify}
          onCancel={handleMFASetupCancel}
        />
      )}
    </div>
  );
};
